import {
  Button,
  ButtonGroup,
  Form,
  FormControl,
  FormLabel,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast
} from '@homebotapp/hb-react-component-catalog'
import { defineMessages, useIntl } from 'react-intl'
import { HOME_SEARCH_SAVED_SEARCH_MANAGE_ALERTS_MODAL } from '../../../constants/tracking/trackingEventLocations/homeSearchTrackingEventLocation'
import {
  getAlertCadenceDays,
  SavedSearchAlertCadence
} from '../Listings/SavedSearchToggle/ConfirmSavedSearchButton/ConfirmSavedSearchModal/ConfirmSavedSearchModal'
import { SavedSearchAlertCadenceRadioButtons } from './SavedSearchAlertCadenceRadioButtons'
import { SavedSearchModel } from '../../../types/savedSearches'
import { useUpdateSavedSearchAlertCadence } from './useUpdateSavedSearchAlertCadence'

export const MSG = defineMessages({
  manageAlerts: {
    id: 'HomeSearch.listings.savedSearch.manageAlertsModal.manageAlerts',
    defaultMessage: 'Manage alerts'
  },
  howOften: {
    id: 'HomeSearch.listings.savedSearch.manageAlertsModal.howOften',
    defaultMessage: 'How often do you want to get alerts?'
  },
  cancel: {
    id: 'HomeSearch.listings.savedSearch.manageAlertsModal.cancel',
    defaultMessage: 'Cancel'
  },
  save: {
    id: 'HomeSearch.listings.savedSearch.manageAlertsModal.save',
    defaultMessage: 'Save'
  },
  toastSuccess: {
    id: 'HomeSearch.listings.savedSearch.manageAlertsModal.toastSuccess',
    defaultMessage: 'Success!'
  },
  toastDaily: {
    id: 'HomeSearch.listings.savedSearch.manageAlertsModal.toastDaily',
    defaultMessage: "We'll send you daily alerts for this search."
  },
  toastWeekly: {
    id: 'HomeSearch.listings.savedSearch.manageAlertsModal.toastWeekly',
    defaultMessage: "We'll send you weekly alerts for this search."
  },
  toastNever: {
    id: 'HomeSearch.listings.savedSearch.manageAlertsModal.toastNever',
    defaultMessage: "We won't send you alerts for this search."
  }
})

interface SavedSearchManageAlertsModalProps {
  savedSearch: SavedSearchModel
  isOpen: boolean
  setIsOpen: (showModal: boolean) => void
}

export const SavedSearchManageAlertsModal = ({ savedSearch, isOpen, setIsOpen }: SavedSearchManageAlertsModalProps) => {
  const intl = useIntl()
  const toast = useToast()
  const { mutateAsync: updateAlertCadence, isLoading: isUpdatingAlertCadence } = useUpdateSavedSearchAlertCadence()

  const getToastDescription = (alertCadence: number | null) => {
    if (alertCadence === 1) {
      return intl.formatMessage(MSG.toastDaily)
    } else if (alertCadence === 7) {
      return intl.formatMessage(MSG.toastWeekly)
    } else {
      return intl.formatMessage(MSG.toastNever)
    }
  }

  const convertAlertCadenceNumberToFormString = (alertCadence: number | null | undefined) => {
    if (alertCadence === 1) {
      return 'daily'
    } else if (alertCadence === 7) {
      return 'weekly'
    } else {
      return 'never'
    }
  }

  interface UpdateSavedSearchAlertCadenceFormData {
    alertCadence: SavedSearchAlertCadence
  }

  const handleSubmit = (data: UpdateSavedSearchAlertCadenceFormData) => {
    if (savedSearch?.id === undefined) {
      return
    }
    updateAlertCadence({
      updateSavedSearchAlertCadenceId: savedSearch?.id,
      input: {
        alertCadenceDays: getAlertCadenceDays(data.alertCadence)
      }
    })
    setIsOpen(false)
    const alertCadence = getAlertCadenceDays(data.alertCadence)
    toast({
      status: 'success',
      title: intl.formatMessage(MSG.toastSuccess),
      description: getToastDescription(alertCadence)
    })
  }

  return (
    <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
      <Form<UpdateSavedSearchAlertCadenceFormData>
        defaultValues={{
          alertCadence: convertAlertCadenceNumberToFormString(savedSearch?.alertCadenceDays)
        }}
        onSubmit={data => handleSubmit(data)}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            <Heading textAlign='left' as='h2' mb={0}>
              {intl.formatMessage(MSG.manageAlerts)}
            </Heading>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>
                <Text color='neutral.700' size='sm'>
                  {intl.formatMessage(MSG.howOften)}
                </Text>
              </FormLabel>
              <SavedSearchAlertCadenceRadioButtons
                tracking={{
                  guid: 'c7432aCvLW393SVTeSPM9z',
                  ui_context: HOME_SEARCH_SAVED_SEARCH_MANAGE_ALERTS_MODAL,
                  descriptor: 'Alert frequency radio button group'
                }}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <ButtonGroup spacing={3}>
              <Button
                tracking={{
                  guid: 'gyfcxajWWF9WPt81sBQkdS',
                  ui_context: HOME_SEARCH_SAVED_SEARCH_MANAGE_ALERTS_MODAL,
                  descriptor: 'Dismiss manage alerts modal'
                }}
                onClick={() => setIsOpen(false)}
              >
                {intl.formatMessage(MSG.cancel)}
              </Button>
              <Button
                type='submit'
                tracking={{
                  guid: 'w5R1Wi3feZRt91Kkp9u8U9',
                  ui_context: HOME_SEARCH_SAVED_SEARCH_MANAGE_ALERTS_MODAL,
                  descriptor: 'Save alert preferences modal button'
                }}
                colorScheme='primary'
              >
                {intl.formatMessage(MSG.save)}
              </Button>
            </ButtonGroup>
          </ModalFooter>
        </ModalContent>
      </Form>
    </Modal>
  )
}
